import { useCallback, useEffect, useState } from 'react';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';

const emptyWarehouse = { name: '', location: '', is_default: false };
const emptyAdjust = { warehouse_id: '', variant_id: '', delta: '', notes: '' };

export default function InventoryPage() {
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [warehouses, setWarehouses] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouseModal, setWarehouseModal] = useState(false);
  const [adjustModal, setAdjustModal] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState(emptyWarehouse);
  const [adjustForm, setAdjustForm] = useState(emptyAdjust);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [warehouseData, stockData] = await Promise.all([
        shopApi.listWarehouses(token, selectedShopId),
        shopApi.listStock(token, selectedShopId),
      ]);
      setWarehouses(warehouseData);
      setStock(stockData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateWarehouse = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.createWarehouse(token, selectedShopId, warehouseForm);
      setWarehouseModal(false);
      setWarehouseForm(emptyWarehouse);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustStock = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.adjustStock(token, selectedShopId, {
        warehouse_id: Number(adjustForm.warehouse_id),
        variant_id: Number(adjustForm.variant_id),
        delta: Number(adjustForm.delta),
        notes: adjustForm.notes || null,
      });
      setAdjustModal(false);
      setAdjustForm(emptyAdjust);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Inventory"
        description="Manage warehouses and stock levels across your catalog."
        actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setAdjustModal(true)}>
              Adjust stock
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setWarehouseModal(true)}>
              Add warehouse
            </button>
          </>
        }
      />

      <ErrorBanner message={error} />

      {loading ? (
        <LoadingState />
      ) : (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="card-body">
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Warehouses</h3>
              {warehouses.length === 0 ? (
                <EmptyState message="No warehouses yet." />
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Location</th>
                      <th>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warehouses.map((warehouse) => (
                      <tr key={warehouse.id}>
                        <td>{warehouse.name}</td>
                        <td>{warehouse.location}</td>
                        <td>{warehouse.is_default ? 'Yes' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Stock</h3>
              {stock.length === 0 ? (
                <EmptyState message="No stock records yet." />
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Variant</th>
                      <th>On hand</th>
                      <th>Reserved</th>
                      <th>Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.map((row) => (
                      <tr key={row.id}>
                        <td>{row.variant_sku || row.product_variant_id}</td>
                        <td>{row.variant_name || '—'}</td>
                        <td>{row.quantity}</td>
                        <td>{row.reserved}</td>
                        <td>{row.available}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      <Modal
        title="Add warehouse"
        open={warehouseModal}
        onClose={() => setWarehouseModal(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setWarehouseModal(false)}>
              Cancel
            </button>
            <button type="submit" form="warehouse-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create'}
            </button>
          </>
        }
      >
        <form id="warehouse-form" className="form-grid" onSubmit={handleCreateWarehouse}>
          <div className="form-field">
            <label htmlFor="warehouse-name">Name</label>
            <input
              id="warehouse-name"
              value={warehouseForm.name}
              onChange={(e) => setWarehouseForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor="warehouse-location">Location</label>
            <input
              id="warehouse-location"
              value={warehouseForm.location}
              onChange={(e) => setWarehouseForm((p) => ({ ...p, location: e.target.value }))}
              required
            />
          </div>
          <label className="form-field">
            <input
              type="checkbox"
              checked={warehouseForm.is_default}
              onChange={(e) => setWarehouseForm((p) => ({ ...p, is_default: e.target.checked }))}
            />{' '}
            Default warehouse
          </label>
        </form>
      </Modal>

      <Modal
        title="Adjust stock"
        open={adjustModal}
        onClose={() => setAdjustModal(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setAdjustModal(false)}>
              Cancel
            </button>
            <button type="submit" form="adjust-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Adjust'}
            </button>
          </>
        }
      >
        <form id="adjust-form" className="form-grid" onSubmit={handleAdjustStock}>
          <div className="form-field">
            <label htmlFor="adjust-warehouse">Warehouse ID</label>
            <input
              id="adjust-warehouse"
              type="number"
              value={adjustForm.warehouse_id}
              onChange={(e) => setAdjustForm((p) => ({ ...p, warehouse_id: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor="adjust-variant">Variant ID</label>
            <input
              id="adjust-variant"
              type="number"
              value={adjustForm.variant_id}
              onChange={(e) => setAdjustForm((p) => ({ ...p, variant_id: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor="adjust-delta">Delta (+/-)</label>
            <input
              id="adjust-delta"
              type="number"
              value={adjustForm.delta}
              onChange={(e) => setAdjustForm((p) => ({ ...p, delta: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor="adjust-notes">Notes</label>
            <input
              id="adjust-notes"
              value={adjustForm.notes}
              onChange={(e) => setAdjustForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>
        </form>
      </Modal>
    </RequireShop>
  );
}
